"use client";

import Link from "next/link";
import { cn } from "@/lib/cn";
import { Icon } from "@/components/ui/Icon";
import { SPORTSBOOK_ROOT } from "@/lib/site";
import { useSite } from "@/lib/api/session";
import { copyrightLine, fillSiteName } from "@/lib/content/footer";
import { AnjouanSeal, isAnjouanSealId } from "./AnjouanSeal";
import type { IconName } from "@/components/ui/Icon";
import { useContent } from "./ContentContext";
import { useSiteName } from "./SiteNameContext";

const SOCIAL_ICON: Record<string, IconName> = {
  x: "x",
  twitter: "x",
  discord: "discord",
  telegram: "telegram",
  facebook: "facebook",
  youtube: "youtube",
};

export function Footer() {
  const { branding, footer, socials, flags, theme } = useContent();
  const site = useSite();
  /* The operator's name is the platform's to state, not the content store's:
     the layout resolves it from /site and the whitelabel's footer builds its
     copyright from the same record. The licence text is admin-written, since
     the API has no field for it, and names the site through a placeholder. */
  const siteName = useSiteName();

  /* The link list is the operator's site record when the platform sends one —
     the whitelabel's footer reads the same positions from its site service —
     and the admin-edited columns otherwise. Two sources for one fact is a
     defect, so the API wins outright the moment it speaks. */
  const apiLinks = site.data?.links ?? [];
  const fromPositions = (position: string) =>
    apiLinks
      .filter((link) => link.positions.includes(position))
      .map((link) => ({ label: link.label, href: link.url, external: /^https?:/i.test(link.url) }));

  const apiColumns = [
    { title: "Platform", links: fromPositions("footer:platform") },
    { title: "Legal", links: fromPositions("footer:legal") },
  ].filter((col) => col.links.length > 0);

  const columns =
    apiColumns.length > 0
      ? apiColumns
      : footer.columns
          .map((col) => ({
            ...col,
            links: col.links.filter((link) => flags.sportsEnabled || link.href !== SPORTSBOOK_ROOT),
          }))
          .filter((col) => col.links.length > 0);

  const apiSocials = fromPositions("footer:socials").map((link) => ({
    label: link.label,
    href: link.href,
    icon: SOCIAL_ICON[link.label.toLowerCase()] ?? ("external" as IconName),
  }));
  const shownSocials = apiSocials.length > 0 ? apiSocials : socials;

  return (
    <footer
      className={cn(
        "flex flex-col items-start justify-center gap-[28px] px-[24px] py-[32px]",
        /* Over a site backdrop the footer is clear, like the rest of the page. */
        theme.backdropUrl?.trim() ? "border-t border-border" : "bg-elevation-1",
        flags.pageWidth === "Centered" ? "lg:px-[max(24px,calc((100%-1144px)/2+24px))]" : "lg:px-[64px]",
      )}
    >
      <div className="flex w-full flex-wrap items-start justify-center gap-[40px]">
        {columns.map((col) => (
          <div key={col.title} className="flex flex-col items-start gap-[10px]">
            <h3 className="w-full text-[14px] font-bold leading-[1.6] text-fg">{col.title}</h3>
            <ul className="flex w-full flex-col items-start gap-[10px]">
              {col.links.map((link) => (
                <li key={link.label} className="flex w-[154px] items-center">
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex h-[22px] flex-1 items-center gap-[4px] whitespace-nowrap text-[14px] font-bold leading-[1.6] text-fg-subtle transition-colors duration-200 hover:text-fg"
                    >
                      {link.label}
                      <Icon name="external" size={16} />
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="flex h-[22px] flex-1 items-center gap-[4px] whitespace-nowrap text-[14px] font-bold leading-[1.6] text-fg-subtle transition-colors duration-200 hover:text-fg"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex w-full items-center justify-center gap-[17px]">
        {shownSocials.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={s.label}
            className="text-fg transition-colors duration-200 hover:text-fg-muted"
          >
            <Icon name={s.icon} size={20} />
          </a>
        ))}
      </div>

      <span className="h-px w-full shrink-0 bg-elevation-3" />

      <p className="w-full text-center text-[14px] font-bold leading-[1.6] text-fg">
        {copyrightLine(siteName, new Date().getFullYear())}
      </p>

      <p className="w-full text-center text-[14px] font-normal leading-[1.6] text-fg-subtle">
        {fillSiteName(branding.footerLegal, siteName)}
      </p>

      <div className="flex w-full items-center justify-center gap-[17px]">
        {isAnjouanSealId(branding.anjouanLicense) ? (
          <AnjouanSeal licenseId={branding.anjouanLicense} />
        ) : (
          <Link
            href="/support/fairness"
            aria-label="Fairness"
            className="grid h-[40px] w-[40px] place-items-center rounded-md text-fg-muted transition-colors hover:text-fg"
          >
            <Icon name="shield-check" size={32} />
          </Link>
        )}
        <Link
          href="/support/responsible-gaming"
          aria-label="Responsible gaming"
          className="grid h-[40px] w-[40px] place-items-center rounded-full border-2 border-fg-muted text-[12px] font-black text-fg-muted transition-colors hover:border-fg hover:text-fg"
        >
          18+
        </Link>
      </div>
    </footer>
  );
}
